import React from "react";

import {
  User,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { getToken, onMessage } from "firebase/messaging";
import { useSearchParams } from "react-router-dom";

import storageKey from "../storageKey";
import useFirebase from "./firebaseContext";
import useNotification from "./notificationContext";

import LoadingPage from "../../pages/LoadingPage";

import useModal from "./modalContext";

import { AuthService } from "../services/AuthService";
import { Dev } from "../util/consoleUtil";

import { OnlyChildren, UserType } from "../../types/common";
import { UserInfoType } from "../../types/database";

type AuthContextType = {
  currentUserState: UserType | null;
  currentUserInfo: UserInfoType | null;
  fetchUserInfo: () => Promise<void>;
  authSignUpUser: (email: string, password: string) => Promise<void>;
  authLogInUser: (email: string, password: string) => Promise<void>;
  authEmailLogInSend: (email: string) => Promise<void>;
  authEmailLogInConfirm: (path: string) => Promise<void>;
  changeUserName: (path: string) => Promise<void>;
  authLogOutUser: () => void;
  authConfirmEmail: () => Promise<void>;
  loadingLocalUserData: boolean;
};

const AuthContext = React.createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: OnlyChildren) => {
  const [searchParams] = useSearchParams();

  const { messaging, db, auth } = useFirebase();
  const { pushAlert } = useModal();
  const { notificationIsActive } = useNotification();

  const authServiceGet = AuthService.One();

  const [loadingLocalUserData, setLoadingLocalUserData] = React.useState<boolean>(
    window.localStorage.getItem(storageKey.IS_LOGGED) ? true : false
  );

  const [rawUserState, setRawUserState] = React.useState<User | null>(null);
  const [currentUserState, setCurrentUserState] = React.useState<UserType | null>(null);
  const [currentUserInfo, setCurrentUserInfo] = React.useState<UserInfoType | null>(null);

  //

  const authSignUpUser = async (email: string, password: string) => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        await createUserWithEmailAndPassword(auth, email, password);

        console.log(...new Dev("authSignUpUser").Purple());

        Ok();
      } catch (error) {
        console.log(...new Dev("authSignUpUser", error).Error());
        Err(error as Error);
      }
    });
  };

  const authLogInUser = async (email: string, password: string) => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        await signInWithEmailAndPassword(auth, email, password);

        console.log(...new Dev("authLogInUser").Purple());

        Ok();
      } catch (error) {
        console.log(...new Dev("authLogInUser", error).Error());
        Err(error as Error);
      }
    });
  };

  const authConfirmEmail = async () => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        await setPersistence(auth, browserLocalPersistence);

        const actionSettings = {
          url: "http://localhost:3000/confirm",
          handleCodeInApp: true,
        };

        console.log(...new Dev("sendEmailVerification").Purple());

        if (rawUserState) await sendEmailVerification(rawUserState, actionSettings);
        else throw new Error("Você precisa estar logado");

        Ok();
      } catch (error) {
        console.log(...new Dev("authLogInUser", error).Error());
        Err(error as Error);
      }
    });
  };

  const authEmailLogInSend = async (email: string) => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        const actionSettings = {
          url: "http://localhost:3000/code?email=" + email,
          handleCodeInApp: true,
          // dynamicLinkDomain: "example.page.link",
        };

        await sendSignInLinkToEmail(auth, email, actionSettings);

        window.localStorage.setItem(storageKey.TMP_EMAIL, email);

        console.log(...new Dev("authEmailLogInSend").Purple());

        Ok();
      } catch (error) {
        console.log(...new Dev("authLogInUser", error).Error());
        Err(error as Error);
      }
    });
  };

  const authEmailLogInConfirm = async (path: string) => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        const email = window.localStorage.getItem(storageKey.TMP_EMAIL) ?? "";
        console.log(...new Dev("Email", email).Purple());

        await setPersistence(auth, browserLocalPersistence);
        await signInWithEmailLink(auth, email, path);

        console.log(...new Dev("authEmailLogInConfirm").Purple());

        Ok();
      } catch (error) {
        console.log(...new Dev("authLogInUser", error).Error());
        Err(error as Error);
      }
    });
  };

  const authLogOutUser = async () => {
    try {
      window.localStorage.removeItem(storageKey.IS_LOGGED);

      await signOut(auth);
      window.location.replace("/");

      console.log(...new Dev("authLogOutUser").Purple());
    } catch (error) {
      console.log(...new Dev("authLogOutUser", error).Error());
      pushAlert({
        type: "error",
        message: (error as Error).message,
      });
    }
  };

  const changeUserName = async (displayName: string) => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        if (!currentUserState) throw new Error("Necessário login");

        const res = await updateProfile(currentUserState, { displayName });

        console.log(...new Dev("changeUserName", res).Purple());

        Ok();
      } catch (error) {
        console.log(...new Dev("changeUserName", error).Error());
        Err(error as Error);
      }
    });
  };

  //

  const fetchUserInfo = async () => {
    return new Promise<void>(async (Ok, Err: (error: Error) => void) => {
      try {
        console.log(...new Dev("fetchUserInfo").Purple());

        const userInfo = await authServiceGet.runRequest(currentUserState?.uid || "");

        setCurrentUserInfo(userInfo || null);

        if (
          userInfo?.type !== currentUserState?.type ||
          userInfo?.enabled !== currentUserState?.enabled
        ) {
          await rawUserState?.getIdToken(true);
        }

        Ok();
      } catch (error) {
        console.log(...new Dev("fetchUserInfo", error).Error());
        if ("cordova" in window) {
          await fetch("https://google.com.br")
            .then(() => {
              pushAlert({
                type: "error",
                message: "Erro ao obter informações de usuário. " + (error as Error).message,
              });
            })
            .catch((err) => console.log(...new Dev("fetchUserInfo NO CONNECTION", err).Error()));
        } else {
          pushAlert({
            type: "error",
            message: "Erro ao obter informações de usuário. " + (error as Error).message,
          });
        }
        Err(error as Error);
      } finally {
        setLoadingLocalUserData(false);
      }
    });
  };

  //

  const updateLastActive = async (currentToken: string) => {
    try {
      const lastActive = parseInt(
        window.localStorage.getItem(storageKey.FCM_TOPICS_LAST_ACTIVE) || "0"
      );

      const now = new Date();

      if (now.getTime() - lastActive > 604800) {
        const subscriptionId = currentToken.split(":")[1].slice(0, 31);
        const subscriptionRef = doc(db, "topicSubscriptions", subscriptionId);
        await setDoc(
          subscriptionRef,
          { lastActiveAtISO: now.toISOString(), lastActiveAtTimestamp: now.getTime() },
          { merge: true }
        );

        window.localStorage.setItem(storageKey.FCM_TOPICS_LAST_ACTIVE, String(now.getTime()));
      }
    } catch (error) {
      console.log(...new Dev("updateLastActive", error).Error());
    }
  };

  const updateUserToken = async () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const currentUserLoggedToken = currentUserInfo?.fcmToken;
        const storageToken = window.localStorage.getItem(storageKey.FCM_TOKEN);

        if ("cordova" in window) {
          (window as any).FirebasePlugin.getToken(
            async (currentToken: string) => {
              updateLastActive(currentToken);

              if (
                currentToken &&
                currentUserLoggedToken === currentToken &&
                currentUserInfo?.fcmToken === currentToken &&
                storageToken === currentToken
              ) {
                return;
              }

              console.log(...new Dev("Current FCM Token:", currentToken).Purple());
              window.localStorage.setItem(storageKey.FCM_TOKEN, currentToken);

              if (currentUserInfo) {
                const userRef = doc(db, "users", String(currentUserState?.uid));
                await setDoc(userRef, { fcmToken: currentToken }, { merge: true });
                setCurrentUserInfo({ ...currentUserInfo, fcmToken: currentToken });
              }
            },
            (error: any) => {
              throw error;
            }
          );
        } else if (messaging) {
          const currentToken = await getToken(messaging, {
            vapidKey: storageKey.PUSH_VAPID_KET,
          });

          if (process.env.REACT_APP_NOTIFICATION_ACTION && window.Notification) {
            onMessage(messaging, (payload) => {
              new window.Notification(payload?.notification?.title || "", {
                body: payload?.notification?.body || "",
              });
            });
          }

          console.log(...new Dev("Current Token", currentToken).Purple());

          updateLastActive(currentToken);

          if (
            currentToken &&
            currentUserLoggedToken === currentToken &&
            currentUserInfo?.fcmToken === currentToken &&
            storageToken === currentToken
          ) {
            return;
          }

          console.log(...new Dev("Current FCM Token:", currentToken).Purple());
          window.localStorage.setItem(storageKey.FCM_TOKEN, currentToken);

          if (currentUserInfo) {
            const userRef = doc(db, "users", String(currentUserState?.uid));
            await setDoc(userRef, { fcmToken: currentToken }, { merge: true });
            setCurrentUserInfo({ ...currentUserInfo, fcmToken: currentToken });
          }
        }
      } catch (error: any) {
        console.log(...new Dev("updateUserToken", error).Error());
        pushAlert({
          type: "error",
          message: "Erro ao abter notificações. Verifique sua conexão. " + error?.message || error,
        });
      }
      resolve();
    });
  };

  //

  React.useEffect(() => {
    console.time("onAuthStateChanged");
  }, []);

  React.useEffect(() => {
    const unsubscribeFromAuthStatuChanged = onAuthStateChanged(auth, async (userObject) => {
      console.timeEnd("onAuthStateChanged");

      if (userObject) {
        console.log(...new Dev("authStateChanged: User just login", userObject).Purple());

        window.localStorage.setItem(storageKey.IS_LOGGED, "true");

        const tokenResult = await userObject.getIdTokenResult();
        const claimType = tokenResult?.claims?.type || "user";
        const claimEnabled = tokenResult?.claims?.enabled;

        setRawUserState(userObject);
        setCurrentUserState({
          ...userObject,
          type: claimType as any,
          enabled: claimEnabled as any,
        });

        if (claimType === "admin") window.localStorage.setItem(storageKey.SHOW_ERROR, "true");
        else window.localStorage.removeItem(storageKey.SHOW_ERROR);
      } else {
        console.log(...new Dev("authStateChanged: Use not logged yed").Purple());
        window.localStorage.removeItem(storageKey.IS_LOGGED);
      }
    });

    return unsubscribeFromAuthStatuChanged;
  }, []);

  React.useEffect(() => {
    if (loadingLocalUserData) {
      const checkLoadingUserTimout = setTimeout(() => {
        setLoadingLocalUserData(false);
      }, 3000);

      return () => {
        clearTimeout(checkLoadingUserTimout);
      };
    }
  }, []);

  React.useEffect(() => {
    if (currentUserState) {
      fetchUserInfo();
    }
  }, [currentUserState]);

  React.useEffect(() => {
    if (currentUserState && currentUserInfo) {
      if (notificationIsActive || "cordova" in window) {
        updateUserToken();
      }
    }
  }, [currentUserInfo]);

  //

  const value = {
    currentUserState,
    currentUserInfo,
    fetchUserInfo,
    authSignUpUser,
    authLogInUser,
    authEmailLogInSend,
    authEmailLogInConfirm,
    authLogOutUser,
    authConfirmEmail,
    changeUserName,
    loadingLocalUserData,
  };

  return (
    <AuthContext.Provider value={value as AuthContextType}>
      {loadingLocalUserData && process.env.REACT_APP_REQUIRE_LOAD_LOGIN === "true" ? (
        <LoadingPage />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  return React.useContext(AuthContext) as AuthContextType;
}
